import Cropper from "react-easy-crop";
import { ImageUp, ZoomIn } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api, readableError } from "@/lib/api";

export default function InsightImageEditor({ insight, onClose, onSaved }) {
  const [title, setTitle] = useState(insight.title);
  const [previewUrl, setPreviewUrl] = useState(insight.image_url);
  const [file, setFile] = useState(null);
  const [crop, setCrop] = useState({ x: insight.crop_x || 0, y: insight.crop_y || 0 });
  const [zoom, setZoom] = useState(insight.crop_zoom || 1);
  const [saving, setSaving] = useState(false);
  const preview = previewUrl;

  const selectFile = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (![
      "image/jpeg",
      "image/png",
      "image/webp",
    ].includes(selected.type) || selected.size > 8 * 1024 * 1024) {
      toast.error("Bitte JPG, PNG oder WEBP bis maximal 8 MB auswählen.");
      return;
    }
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      let storagePath;
      if (file) {
        const uploadData = new FormData();
        uploadData.append("file", file);
        const upload = await api.post("/admin/uploads/setup-insights", uploadData);
        storagePath = upload.data.storage_path;
      }
      const response = await api.put(`/admin/setup-insights/${insight.id}`, {
        title,
        ...(storagePath ? { storage_path: storagePath } : {}),
        crop_x: Math.max(-100, Math.min(100, crop.x)),
        crop_y: Math.max(-100, Math.min(100, crop.y)),
        crop_zoom: zoom,
      });
      onSaved(response.data);
      toast.success("Einblick aktualisiert.");
      onClose();
    } catch (error) {
      toast.error(readableError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="insight-image-editor-modal">
      <form className="modal insight-image-editor" onSubmit={save}>
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          data-testid="close-insight-image-editor-button"
        >
          ×
        </button>
        <p className="eyebrow">EINBLICK AKTUALISIEREN</p>
        <h2>{insight.title}</h2>
        <label>
          Titel
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            data-testid="insight-title-input"
          />
        </label>
        <label className="image-upload-control" data-testid="insight-image-upload-label">
          <ImageUp size={18} />
          Neues Bild auswählen
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={selectFile}
            data-testid="insight-image-upload-input"
          />
        </label>
        <div className="crop-stage" data-testid="insight-crop-stage">
          <Cropper
            image={preview}
            crop={crop}
            zoom={zoom}
            aspect={4 / 3}
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
          />
        </div>
        <label className="zoom-control">
          <ZoomIn size={17} /> Zoom
          <input
            type="range"
            min="1"
            max="3"
            step="0.05"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            data-testid="insight-crop-zoom-input"
          />
        </label>
        <p className="crop-hint" data-testid="insight-crop-hint">
          Bild direkt im Raster verschieben und mit dem Regler heranzoomen.
        </p>
        <button
          className="button button-primary"
          disabled={saving}
          data-testid="save-insight-image-button"
        >
          {saving ? "Wird gespeichert…" : "Bild übernehmen"}
        </button>
      </form>
    </div>
  );
}